// Module 14 and 15 contracts (ADR-019, safety & reporting stream): occupancy, presence review,
// presence sources, muster, roll call, roster exports, reports, exports, schedules and audit search.
// Messages are i18n keys (PD-015).
import { z } from 'zod';

const Uuid = z.uuid();
const trimmed = (max: number) => z.string().trim().min(1, 'v.required').max(max, 'v.tooLong');
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, 'v.tooLong')
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const LocalDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'v.date');
const Version = z.number().int().min(1);
const Instant = z.iso.datetime({ offset: true });

// ------------------------------------------------------------------ occupancy (PB-14-01, PB-14-02, PB-14-08)
export const OccupancyQuery = z.object({ siteId: Uuid, locationId: Uuid.optional(), q: z.string().trim().max(100).optional() });

export interface SourceStatusDto {
  readonly id: string | null;
  readonly key: string;
  readonly label: string;
  readonly kind: string;
  readonly mode: 'synchronous' | 'feed';
  readonly status: 'active' | 'disabled';
  readonly freshness: 'current' | 'stale' | 'never_reported' | 'disabled';
  readonly confirmedThrough: string | null;
  readonly lastHeartbeatAt: string | null;
  readonly version: number | null;
}

export interface FreshnessDto {
  /** Earliest confirmed-through of active feeds; null = no feed or one never reported. */
  readonly watermark: string | null;
  readonly stale: boolean;
  readonly staleSources: readonly string[];
  readonly feeds: number;
  readonly neverReported: number;
  /** Latest presence observation recorded at the site (any source). */
  readonly lastRecordedAt: string | null;
  readonly sources: readonly SourceStatusDto[];
}

export interface OccupantDto {
  readonly participationId: string;
  readonly visitId: string;
  readonly reference: string;
  readonly name: string;
  readonly organisation: string | null;
  readonly subjectKind: 'visitor' | 'contractor';
  readonly category: string | null;
  readonly host: { readonly id: string; readonly name: string };
  readonly destination: { readonly id: string; readonly name: string } | null;
  readonly occupancy: 'onsite' | 'uncertain';
  readonly presenceStatus: string;
  readonly lastObservedAt: string | null;
  readonly lastSource: string | null;
  readonly overdue: boolean;
  readonly openReviews: number;
  /** Independent facts, shown as such: never used to add or remove anyone. */
  readonly planningStatus: string;
  readonly credentialStatus: string;
  readonly windowEnd: string | null;
}

export interface CourierDto {
  readonly deliveryId: string;
  readonly courier: string;
  readonly driver: string | null;
  readonly arrivedAt: string | null;
}

export interface VehicleOnsiteDto {
  readonly vehicleId: string;
  readonly plate: string;
  readonly jurisdiction: string;
  readonly presenceStatus: string;
  readonly lastMovementAt: string | null;
  readonly visitId: string | null;
  readonly hostName: string | null;
}

export interface OccupancyResponse {
  readonly siteId: string;
  readonly timeZone: string;
  readonly asOf: string;
  readonly freshness: FreshnessDto;
  readonly counts: { readonly onsite: number; readonly uncertain: number; readonly overdue: number; readonly couriers: number; readonly vehicles: number; readonly openReviews: number };
  readonly people: readonly OccupantDto[];
  readonly couriers: readonly CourierDto[];
  readonly vehicles: readonly VehicleOnsiteDto[];
  readonly canReview: boolean;
  readonly canStartMuster: boolean;
  readonly openMusterId: string | null;
}

// ------------------------------------------------------------------ presence review (PB-14-02)
export const PRESENCE_REVIEW_RESOLUTIONS = ['confirmed_onsite', 'confirmed_departed', 'remains_unknown', 'no_change'] as const;
export const OpenReviewRequest = z.object({ participationId: Uuid, reason: trimmed(500) });
export const ResolveReviewRequest = z.object({ resolution: z.enum(PRESENCE_REVIEW_RESOLUTIONS), note: trimmed(1000), expectedVersion: Version });
export const ReviewListQuery = z.object({ siteId: Uuid, status: z.enum(['open', 'resolved', 'all']).default('open') });

export interface PresenceReviewDto {
  readonly id: string;
  readonly siteId: string;
  readonly participationId: string;
  readonly visitId: string;
  readonly reference: string;
  readonly name: string;
  readonly anomaly: string;
  readonly detail: Readonly<Record<string, unknown>>;
  readonly status: 'open' | 'resolved';
  readonly openedAt: string;
  readonly openedBy: string | null;
  readonly openReason: string | null;
  readonly resolution: string | null;
  readonly resolutionNote: string | null;
  readonly presenceBefore: string | null;
  readonly presenceAfter: string | null;
  readonly resolvedAt: string | null;
  readonly resolvedBy: string | null;
  readonly presenceStatus: string;
  readonly version: number;
}

export interface PresenceHistoryResponse {
  readonly participationId: string;
  readonly siteId: string;
  readonly timeZone: string;
  readonly presenceStatus: string;
  /** Independent facts beside presence (PB-14-02): none of them changes presence. */
  readonly facts: { readonly planning: string; readonly approval: string; readonly credential: string; readonly expected: { readonly start: string | null; readonly end: string | null } };
  readonly events: ReadonlyArray<{ readonly id: string; readonly kind: string; readonly observedAt: string; readonly recordedAt: string; readonly source: string; readonly recordedBy: string | null; readonly late: boolean }>;
  readonly reviews: readonly PresenceReviewDto[];
}

// ------------------------------------------------------------------ presence sources (PB-14-08)
export const PRESENCE_SOURCE_KINDS = ['reception', 'kiosk', 'gate', 'access_control', 'edge_agent', 'other'] as const;
export const CreateSourceRequest = z.object({
  siteId: Uuid,
  key: z.string().trim().regex(/^[a-z0-9][a-z0-9._-]{0,63}$/, 'saf.v.sourceKey'),
  label: trimmed(120),
  kind: z.enum(PRESENCE_SOURCE_KINDS),
  mode: z.enum(['synchronous', 'feed']),
  expectedIntervalSeconds: z.number().int().min(5).max(86400).nullable().optional(),
});
export const UpdateSourceRequest = z.object({ label: trimmed(120).optional(), status: z.enum(['active', 'disabled']).optional(), expectedVersion: Version });
export const HeartbeatRequest = z.object({ confirmedThrough: Instant });

// ------------------------------------------------------------------ settings (PD-233)
export const SafetySettingsBody = z.object({
  staleAfterMinutes: z.number().int().min(1).max(1440),
  notifyHostsOnEmergency: z.boolean(),
  notifyHostsOnDrill: z.boolean(),
  exportTtlMinutes: z.number().int().min(1).max(1440),
  defaultAssemblyPoint: z.string().trim().max(200, 'v.tooLong').nullable(),
  includeCouriers: z.boolean(),
  noShowAfterMinutes: z.number().int().min(0).max(1440).nullable(),
});
export const PutSafetySettingsRequest = z.object({ siteId: Uuid, settings: SafetySettingsBody, expectedVersion: z.number().int().min(0) });
export interface SafetySettingsResponse {
  readonly siteId: string;
  readonly settings: z.infer<typeof SafetySettingsBody>;
  /** 0 = defaults, never saved. */
  readonly version: number;
  readonly canEdit: boolean;
}

// ------------------------------------------------------------------ muster (PB-14-03 .. PB-14-07)
export const MUSTER_KINDS = ['drill', 'emergency'] as const;
export const ROLL_CALL_STATUS_VALUES = ['safe', 'missing', 'departed', 'unknown', 'needs_assistance'] as const;
export const ROLL_CALL_METHOD_VALUES = ['in_person', 'radio', 'phone', 'list_check', 'other'] as const;

export const StartMusterRequest = z.object({
  siteId: Uuid,
  kind: z.enum(MUSTER_KINDS),
  assemblyPoint: optionalText(200),
  note: optionalText(1000),
  /** The coordinator saw the freshness warning and confirms (PB-14-03). */
  confirm: z.literal(true, { message: 'saf.v.confirm' }),
});
export const MusterListQuery = z.object({ siteId: Uuid });
export const MusterRosterQuery = z.object({
  q: z.string().trim().max(100).optional(),
  zone: Uuid.optional(),
  kind: z.enum(['visitor', 'contractor', 'courier']).optional(),
  status: z.enum([...ROLL_CALL_STATUS_VALUES, 'not_checked', 'conflict', 'unaccounted']).optional(),
  hostId: Uuid.optional(),
  organisation: z.string().trim().max(200).optional(),
});
export const ObservationRequest = z.object({
  status: z.enum(ROLL_CALL_STATUS_VALUES),
  method: z.enum(ROLL_CALL_METHOD_VALUES),
  note: optionalText(500),
  /** The observation the observer saw as current (null when none). */
  basisId: Uuid.nullable(),
  reconciles: z.boolean().default(false),
  observedAt: Instant.optional(),
});
export const CloseMusterRequest = z.object({ reason: trimmed(1000), expectedVersion: Version });
export const MUSTER_EXPORT_FORMATS = ['csv', 'html', 'pdf'] as const; // pdf: PD-635
export const MusterExportRequest = z.object({ format: z.enum(MUSTER_EXPORT_FORMATS), filters: MusterRosterQuery.default({}) });

export interface RollCallDto {
  readonly status: 'safe' | 'missing' | 'departed' | 'unknown' | 'needs_assistance' | 'not_checked' | 'conflict';
  readonly headId: string | null;
  readonly conflictIds: readonly string[];
  readonly lastObservedAt: string | null;
  readonly lastObserver: string | null;
}

export interface RosterEntryDto {
  readonly id: string;
  readonly subjectKind: 'visitor' | 'contractor' | 'courier';
  readonly participationId: string | null;
  readonly deliveryId: string | null;
  readonly name: string;
  readonly organisation: string | null;
  readonly category: string | null;
  readonly host: { readonly id: string | null; readonly name: string | null };
  readonly destination: { readonly id: string | null; readonly name: string | null };
  readonly occupancyAtSnapshot: 'onsite' | 'uncertain';
  readonly lastObservedAt: string | null;
  readonly overdueAtSnapshot: boolean;
  readonly openReviewAtSnapshot: boolean;
  readonly rollCall: RollCallDto;
  /** Presence observations recorded after the snapshot (PB-14-06). */
  readonly lateFacts: ReadonlyArray<{ readonly kind: string; readonly observedAt: string; readonly recordedAt: string }>;
}

export interface MusterTotalsDto {
  readonly roster: number;
  readonly accounted: number;
  readonly unaccounted: number;
  readonly byStatus: Readonly<Record<RollCallDto['status'], number>>;
}

export interface MusterSummaryDto {
  readonly id: string;
  readonly siteId: string;
  readonly kind: 'drill' | 'emergency';
  readonly status: 'open' | 'closed';
  readonly assemblyPoint: string | null;
  readonly note: string | null;
  readonly startedAt: string;
  readonly startedBy: string;
  readonly snapshotAt: string;
  readonly closedAt: string | null;
  readonly closedBy: string | null;
  readonly closeReason: string | null;
  readonly unresolvedDepartures: number;
  readonly openReviews: number;
  readonly freshness: FreshnessDto;
  readonly version: number;
  readonly totals: MusterTotalsDto;
}

export interface MusterDetailResponse {
  readonly muster: MusterSummaryDto;
  readonly timeZone: string;
  /** Totals for the whole event — never relabelled by a filter (PB-14-05). */
  readonly totals: MusterTotalsDto;
  /** Totals for the filtered entries only, labelled as such. */
  readonly filtered: MusterTotalsDto | null;
  readonly entries: readonly RosterEntryDto[];
  readonly vehicles: readonly VehicleOnsiteDto[];
  /** People observed arriving after the snapshot who are not on it. */
  readonly arrivedAfterSnapshot: ReadonlyArray<{ readonly participationId: string; readonly name: string; readonly observedAt: string }>;
  readonly notices: { readonly queued: number; readonly accepted: number; readonly failed: number; readonly blocked: number };
  readonly freshnessNow: FreshnessDto;
  readonly can: { readonly observe: boolean; readonly close: boolean; readonly export: boolean };
}

export interface MusterHistoryResponse {
  readonly muster: MusterSummaryDto;
  readonly timeZone: string;
  readonly observations: ReadonlyArray<{ readonly id: string; readonly entryId: string; readonly name: string; readonly status: string; readonly method: string; readonly note: string | null; readonly basisId: string | null; readonly reconciles: boolean; readonly observer: string; readonly observedAt: string; readonly recordedAt: string }>;
  readonly lateFacts: ReadonlyArray<{ readonly participationId: string; readonly name: string; readonly kind: string; readonly observedAt: string; readonly recordedAt: string; readonly inRoster: boolean }>;
  readonly exports: ReadonlyArray<{ readonly id: string; readonly format: string; readonly asOf: string; readonly requestedBy: string; readonly expiresAt: string; readonly rowCount: number }>;
}

export interface MusterExportResponse {
  readonly id: string;
  readonly format: (typeof MUSTER_EXPORT_FORMATS)[number];
  readonly asOf: string;
  readonly expiresAt: string;
  readonly rowCount: number;
  readonly downloadUrl: string;
}

// ------------------------------------------------------------------ reports (PB-15-01 .. PB-15-10)
export const REPORT_GROUPING_VALUES = ['none', 'site', 'day', 'category', 'host', 'department', 'organisation', 'destination', 'purpose', 'source'] as const;
const IdList = z.array(Uuid).max(200).default([]);
const CodeList = z.array(z.string().trim().min(1).max(60)).max(100).default([]);
export const ReportFilterInput = z.object({
  definition: z.string().regex(/^[a-z][a-z0-9_.]{1,63}$/, 'v.invalid'),
  siteIds: z.array(Uuid).min(1, 'rpt.v.sites').max(200),
  from: LocalDate,
  to: LocalDate,
  timeBasis: z.enum(['site_local', 'utc']).default('site_local'),
  groupBy: z.enum(REPORT_GROUPING_VALUES).default('none'),
  attribution: z.enum(['historical', 'current']).default('historical'),
  locationIds: IdList,
  categoryCodes: CodeList,
  organisationIds: IdList,
  hostIds: IdList,
  states: CodeList,
});
export type ReportFilterInput = z.infer<typeof ReportFilterInput>;
export const RunReportRequest = z.object({ filter: ReportFilterInput, detail: z.boolean().default(false), cursor: z.string().max(100).optional(), pageSize: z.number().int().min(10).max(500).default(100) });
export const REPORT_EXPORT_FORMATS = ['csv', 'xlsx', 'html', 'pdf'] as const; // pdf: native PDF with Arabic shaping (PD-635)
export const ReportExportRequest = z.object({ filter: ReportFilterInput, detail: z.boolean().default(false), format: z.enum(REPORT_EXPORT_FORMATS), displayedDigest: z.string().min(16).max(64), reason: optionalText(500) });

export interface ReportDefinitionDto {
  readonly key: string;
  readonly title: string;
  readonly description: string;
  readonly identifiable: 'never' | 'optional' | 'always';
  readonly groupings: readonly string[];
  readonly filters: readonly string[];
  readonly states: readonly string[];
  readonly attribution: boolean;
  readonly entitlement: string | null;
  readonly available: boolean;
}

export interface ReportsCatalogueResponse {
  readonly definitions: readonly ReportDefinitionDto[];
  readonly sites: ReadonlyArray<{ readonly id: string; readonly name: string; readonly timeZone: string; readonly detail: boolean; readonly export: boolean; readonly schedule: boolean }>;
  readonly multiSite: boolean;
}

export interface ReportColumnDto {
  readonly key: string;
  readonly label: string;
  readonly kind: string;
  readonly statePrefix: string | null;
  readonly zoneField: string | null;
}

export interface ReportRunResponse {
  readonly filter: ReportFilterInput;
  readonly digest: string;
  readonly title: string;
  readonly generatedAt: string;
  readonly identifiable: boolean;
  readonly columns: readonly ReportColumnDto[];
  readonly rows: ReadonlyArray<Readonly<Record<string, string | number | null>>>;
  readonly metrics: ReadonlyArray<{ readonly key: string; readonly label: string; readonly definition: string; readonly value: number | null; readonly basis: 'period' | 'now' }>;
  readonly notes: readonly string[];
  readonly completeness: { readonly asOf: string; readonly openPresenceReviews: number; readonly classifiedAt: string | null; readonly unavailable: readonly string[] };
  readonly truncated: boolean;
  readonly scope: ReadonlyArray<{ readonly id: string; readonly name: string; readonly timeZone: string }>;
  readonly page: { readonly offset: number; readonly total: number; readonly nextCursor: string | null; readonly reset: boolean };
}

export interface ExportJobDto {
  readonly id: string;
  readonly kind: 'report' | 'audit';
  readonly definition: string;
  readonly format: string;
  readonly status: 'queued' | 'ready' | 'failed' | 'denied';
  readonly statusReason: string | null;
  readonly expired: boolean;
  readonly rowCount: number | null;
  readonly byteSize: number | null;
  readonly requestedAt: string;
  readonly completedAt: string | null;
  readonly expiresAt: string;
  readonly digest: string;
  readonly downloadUrl: string | null;
}

export const SCHEDULE_CADENCES = ['daily', 'weekly', 'monthly'] as const;
export const SCHEDULE_PERIODS = ['previous_day', 'previous_7_days', 'previous_month'] as const;
export const CreateScheduleRequest = z.object({
  name: trimmed(120),
  filter: ReportFilterInput,
  format: z.enum(REPORT_EXPORT_FORMATS),
  cadence: z.enum(SCHEDULE_CADENCES),
  weekday: z.number().int().min(1).max(7).nullable().optional(),
  monthDay: z.number().int().min(1).max(28).nullable().optional(),
  localTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'v.time'),
  timeZone: z.string().min(1).max(64),
  period: z.enum(SCHEDULE_PERIODS),
  recipientUserIds: z.array(Uuid).min(1, 'rpt.v.recipients').max(50),
});
export const UpdateScheduleRequest = z.object({ status: z.enum(['active', 'disabled']), reason: optionalText(500), expectedVersion: Version });

export interface ReportScheduleDto {
  readonly id: string;
  readonly name: string;
  readonly definition: string;
  readonly filter: ReportFilterInput;
  readonly format: string;
  readonly cadence: string;
  readonly weekday: number | null;
  readonly monthDay: number | null;
  readonly localTime: string;
  readonly timeZone: string;
  readonly period: string;
  readonly status: 'active' | 'disabled';
  readonly nextRunAt: string | null;
  readonly lastRunAt: string | null;
  readonly recipients: ReadonlyArray<{ readonly id: string; readonly name: string; readonly authorisedNow: boolean }>;
  readonly owner: { readonly id: string; readonly name: string };
  readonly version: number;
}

export interface ScheduleRunsResponse {
  readonly runs: ReadonlyArray<{
    readonly id: string;
    readonly dueAt: string;
    readonly ranAt: string;
    readonly outcome: string;
    readonly ownerAuthorised: boolean;
    readonly deliveries: ReadonlyArray<{ readonly recipient: string; readonly decision: string; readonly withheldReason: string | null; readonly noticeStatus: string; readonly exportJobId: string | null }>;
  }>;
}

// ------------------------------------------------------------------ audit search and export (PB-15-11 .. PB-15-14)
export const AuditSearchQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  after: z.string().max(200).optional(),
  siteId: Uuid.optional(),
  from: Instant.optional(),
  to: Instant.optional(),
  action: z.string().trim().regex(/^[a-z][a-z0-9_.-]{0,99}$/, 'v.invalid').optional(),
  outcome: z.enum(['succeeded', 'denied', 'failed']).optional(),
  actorUserId: Uuid.optional(),
  resourceType: z.string().trim().regex(/^[a-z][a-z0-9_.-]{0,59}$/, 'v.invalid').optional(),
  resourceId: z.string().trim().max(100).optional(),
  correlationId: z.string().trim().max(100).optional(),
});
export const AuditExportRequest = z.object({
  from: Instant,
  to: Instant,
  siteIds: z.array(Uuid).max(200).default([]),
  action: z.string().trim().regex(/^[a-z][a-z0-9_.-]{0,99}$/, 'v.invalid').optional(),
  outcome: z.enum(['succeeded', 'denied', 'failed']).optional(),
  actorUserId: Uuid.optional(),
  resourceType: z.string().trim().regex(/^[a-z][a-z0-9_.-]{0,59}$/, 'v.invalid').optional(),
  resourceId: z.string().trim().max(100).optional(),
  correlationId: z.string().trim().max(100).optional(),
  includeChanges: z.boolean().default(false),
  format: z.enum(REPORT_EXPORT_FORMATS),
  reason: trimmed(500),
});

export interface CorrelationResponse {
  readonly correlationId: string;
  readonly audit: ReadonlyArray<{ readonly id: string; readonly occurredAt: string; readonly action: string; readonly outcome: string; readonly resourceType: string; readonly resourceId: string | null; readonly siteId: string | null }>;
  /** Outbox jobs and provider references: only for organisation-wide auditors (no site attribution). */
  readonly jobs: ReadonlyArray<{ readonly id: string; readonly topic: string; readonly attempts: number; readonly state: string; readonly createdAt: string; readonly lastError: string | null }>;
  readonly deliveries: ReadonlyArray<{ readonly kind: string; readonly id: string; readonly status: string; readonly providerReference: string | null; readonly attempts: number }>;
  readonly organisationWide: boolean;
  // PB-15-13 (messaging stream, PD-533 – PD-535): external records linked to the command. Each source
  // is listed only for a caller holding that source's read right on the record's site (or the
  // organisation); `withheld` names the sources not shown for lack of that right.
  /** Credential provider operations and adapter operations, with the reconciliation route when unresolved. */
  readonly operations: ReadonlyArray<CorrelationOperation>;
  /** Identity verification transactions (UAE PASS); digests and claims are never shown. */
  readonly verifications: ReadonlyArray<CorrelationVerification>;
  /** Outbound webhook deliveries of this command's events, with every attempt. */
  readonly webhooks: ReadonlyArray<CorrelationWebhook>;
  /** Inbound provider events: access events of the linked credentials, camera reads recorded by the command. */
  readonly inbound: ReadonlyArray<CorrelationInbound>;
  /** Later receipts for the same records under other correlation ids (provider callbacks, reconciliations). */
  readonly receipts: ReadonlyArray<{ readonly id: string; readonly correlationId: string | null; readonly occurredAt: string; readonly action: string; readonly outcome: string; readonly resourceType: string; readonly resourceId: string | null; readonly siteId: string | null }>;
  readonly withheld: ReadonlyArray<CorrelationSource>;
}

export type CorrelationSource = 'jobs' | 'credential_operations' | 'adapter_operations' | 'verifications' | 'webhooks' | 'access_events' | 'camera_reads';

export interface CorrelationOperation {
  readonly source: 'credential' | 'adapter';
  readonly id: string;
  readonly siteId: string | null;
  /** issue / revocation (credential) or the adapter operation name. */
  readonly kind: string;
  /** Provider outcome as recorded; `unknown` is never shown as success. */
  readonly state: string;
  readonly attempts: number | null;
  readonly createdAt: string;
  readonly settledAt: string | null;
  readonly resolution: string | null;
  /** Web route where a person reconciles an unresolved outcome; null when nothing is left to do. */
  readonly reconcile: string | null;
}

export interface CorrelationVerification {
  readonly id: string;
  readonly siteId: string;
  readonly provider: string;
  readonly kind: string;
  readonly status: string;
  readonly statusReason: string | null;
  readonly createdAt: string;
  readonly completedAt: string | null;
  readonly reconcile: string | null;
}

export interface CorrelationWebhook {
  readonly id: string;
  readonly eventId: string;
  readonly eventType: string;
  readonly status: string;
  readonly attempts: ReadonlyArray<{ readonly attemptNo: number; readonly outcome: string; readonly statusCode: number | null; readonly startedAt: string; readonly replay: boolean }>;
  readonly deliveredAt: string | null;
  readonly reconcile: string | null;
}

export interface CorrelationInbound {
  readonly source: 'access_event' | 'camera_read';
  readonly id: string;
  readonly siteId: string;
  readonly direction: string;
  /** granted / denied (access) or the camera read outcome. */
  readonly result: string;
  readonly occurredAt: string;
  readonly receivedAt: string;
  readonly late: boolean;
}
